import { inject, Injectable } from '@angular/core';

import { Store } from '@ngrx/store';
import { combineLatest, distinctUntilChanged } from 'rxjs';
import { map } from 'rxjs/operators';

import { AttachmentsSelectors } from '@recruitee/attachments-data';
import type { Attachment, AttachmentKind, AttachmentSource } from '@recruitee/attachments-types';
import { filterBoolean, filterDefined } from '@recruitee/common';

@Injectable({ providedIn: 'root' })
export class AttachmentsService {
  private store = inject(Store);

  public getAttachmentsSplittedBySourceAndKind(
    scope: string,
    source: AttachmentSource,
    kind: AttachmentKind,
  ) {
    return this.getAttachmentsByScope(scope).pipe(
      map(attachments => {
        const filterCondition = (attachment: Attachment) =>
          attachment.source === source && attachment.kind === kind;

        return {
          attachments: attachments,
          attachmentsFromSource: attachments.filter(attachment => filterCondition(attachment)),
          attachmentsAsideFromSource: attachments.filter(
            attachment => !filterCondition(attachment),
          ),
        };
      }),
    );
  }

  public hasAttachmentsLoaded(scope: string) {
    const isLoaded$ = this.store
      .select(
        AttachmentsSelectors.isLoaded({
          scope,
        }),
      )
      .pipe(filterDefined);
    const isPending$ = this.store
      .select(
        AttachmentsSelectors.isPending({
          scope,
        }),
      )
      .pipe(filterDefined);

    return combineLatest([isLoaded$, isPending$]).pipe(
      map(([isLoaded, isPending]) => !isPending && isLoaded),
      distinctUntilChanged(),
    );
  }

  private getAttachmentsByScope(scope: string) {
    return this.store
      .select(
        AttachmentsSelectors.entities({
          scope,
        }),
      )
      .pipe(filterBoolean);
  }
}
